import { useState, useEffect } from "react";
import {
  Container, Header, Table, Button, SpaceBetween, Box, Badge,
  Modal, FormField, Input, Select, Alert, Pagination, TextFilter,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";
import { useAuth } from "../../context/AuthContext";

const LEVEL_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "super", label: "Super Admin" },
];

export default function AdminUsers() {
  const { adminUser } = useAuth();
  const isSuper = adminUser?.admin_level === "super";
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", displayName: "", password: "", confirmPassword: "", adminLevel: LEVEL_OPTIONS[0] });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setAdmins(await adminService.getAdminUsers());
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load admin users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ email: "", displayName: "", password: "", confirmPassword: "", adminLevel: LEVEL_OPTIONS[0] });
    setFormError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowCreate(true);
  };

  const handleCreate = async () => {
    setFormError("");
    if (!form.email || !form.displayName || !form.password) {
      setFormError("All fields are required.");
      return;
    }
    if (form.password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }
    setCreating(true);
    try {
      await adminService.createAdminUser(form.email, form.displayName, form.password, form.adminLevel.value);
      setSuccess(`Admin user "${form.email}" created successfully.`);
      setShowCreate(false);
      load();
    } catch (e) {
      setFormError(e.response?.data?.detail || "Failed to create admin user.");
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (a) => {
    if (!window.confirm(`Deactivate admin "${a.email}"? They will no longer be able to log in.`)) return;
    try {
      await adminService.deactivateAdminUser(a.admin_user_id);
      setSuccess(`Admin "${a.email}" deactivated.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to deactivate admin.");
    }
  };

  const handleReactivate = async (a) => {
    if (!window.confirm(`Reactivate admin "${a.email}"? They will be able to log in again.`)) return;
    try {
      await adminService.reactivateAdminUser(a.admin_user_id);
      setSuccess(`Admin "${a.email}" reactivated.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reactivate admin.");
    }
  };

  const filtered = admins.filter(a => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return a.email?.toLowerCase().includes(q) || a.display_name?.toLowerCase().includes(q);
  });
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const isSelf = (a) => a.email === adminUser?.email;

  const cols = [
    {
      id: "name", header: "Admin User",
      cell: a => (
        <SpaceBetween size="xs" direction="vertical">
          <Box fontWeight="bold">{a.display_name}</Box>
          <Box fontSize="body-s" color="text-status-subdued">{a.email}</Box>
        </SpaceBetween>
      ),
    },
    {
      id: "level", header: "Level",
      cell: a => <Badge color={a.admin_level === "super" ? "red" : "blue"}>{a.admin_level}</Badge>,
      width: 120,
    },
    {
      id: "status", header: "Status",
      cell: a => <Badge color={a.is_active ? "green" : "grey"}>{a.is_active ? "Active" : "Inactive"}</Badge>,
      width: 100,
    },
    {
      id: "last_login", header: "Last Login",
      cell: a => a.last_login ? new Date(a.last_login).toLocaleString() : "Never",
    },
    {
      id: "created", header: "Created",
      cell: a => a.created_at ? new Date(a.created_at).toLocaleDateString() : "—",
      width: 120,
    },
    {
      id: "actions", header: "Actions", width: 130,
      cell: a => {
        if (isSelf(a)) return <Box color="text-status-subdued" fontSize="body-s">Current User</Box>;
        const noPermission = !isSuper && a.admin_level === "super";
        if (!a.is_active) {
          return (
            <Button size="small" variant="normal" disabled={noPermission}
              onClick={() => handleReactivate(a)}>
              {noPermission ? "No permission" : "Reactivate"}
            </Button>
          );
        }
        return (
          <Button size="small" variant="normal" disabled={noPermission}
            onClick={() => handleDeactivate(a)}>
            {noPermission ? "No permission" : "Deactivate"}
          </Button>
        );
      },
    },
  ];

  return (
    <Container header={
      <Header variant="h2" description="Manage admin portal users"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={load} loading={loading}>Refresh</Button>
            <Button variant="primary" onClick={openCreate}>Create Admin User</Button>
          </SpaceBetween>
        }>
        Admin Users
      </Header>
    }>
      <SpaceBetween size="m">
        {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess("")}>{success}</Alert>}

        <Table
          columnDefinitions={cols}
          items={paginated}
          loading={loading}
          loadingText="Loading admin users..."
          filter={
            <TextFilter
              filteringText={filter}
              onChange={({ detail }) => { setFilter(detail.filteringText); setPage(1); }}
              filteringPlaceholder="Search by email or name..."
            />
          }
          pagination={
            <Pagination
              currentPageIndex={page}
              pagesCount={Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
            />
          }
          empty={
            <Box textAlign="center" color="text-body-secondary">
              No admin users found.
            </Box>
          }
        />

        <Modal
          visible={showCreate}
          onDismiss={() => setShowCreate(false)}
          header="Create Admin User"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleCreate} loading={creating}>Create</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            {formError && <Alert type="error">{formError}</Alert>}
            <FormField label="Email">
              <Input
                value={form.email}
                onChange={({ detail }) => setForm(f => ({ ...f, email: detail.value }))}
                placeholder="admin@iwms.com"
                type="email"
              />
            </FormField>
            <FormField label="Display Name">
              <Input
                value={form.displayName}
                onChange={({ detail }) => setForm(f => ({ ...f, displayName: detail.value }))}
                placeholder="e.g. John Smith"
              />
            </FormField>
            {isSuper && (
              <FormField label="Admin Level">
                <Select
                  selectedOption={form.adminLevel}
                  onChange={({ detail }) => setForm(f => ({ ...f, adminLevel: detail.selectedOption }))}
                  options={LEVEL_OPTIONS}
                />
              </FormField>
            )}
            <FormField label="Password">
              <div style={{ position: "relative" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={({ detail }) => setForm(f => ({ ...f, password: detail.value }))}
                  placeholder="Minimum 8 characters"
                />
                <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
                  <Button variant="link" formAction="none" onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
            </FormField>
            <FormField label="Confirm Password">
              <div style={{ position: "relative" }}>
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={({ detail }) => setForm(f => ({ ...f, confirmPassword: detail.value }))}
                  placeholder="Re-enter password"
                />
                <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
                  <Button variant="link" formAction="none" onClick={() => setShowConfirmPassword(p => !p)}>
                    {showConfirmPassword ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
            </FormField>
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </Container>
  );
}
